import { Switch, type SwitchProps } from '@mantine/core';
import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { useErrorTranslation } from '../Inputs/hooks/useErrorTranslation';

export type SwitchFieldProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<SwitchProps, 'value' | 'defaultValue'>;

/**
 * SwitchField component
 *
 * @example
 * ```tsx
 * <SwitchField name="field.name" required />
 * ```
 *
 * @public
 * @param name - field name
 * @param control - form control
 * @param defaultValue - default value
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param onChange - onChange event
 * @param props - other props
 */
export function SwitchField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  onChange,
  ...props
}: SwitchFieldProps<T>) {
  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  const errorMessage = useErrorTranslation(false, fieldState.error?.message);

  return (
    <Switch
      error={errorMessage}
      checked={value}
      value={value}
      onChange={e => {
        fieldOnChange(e);
        onChange?.(e);
      }}
      {...field}
      {...props}
    />
  );
}
